import { Box } from '@chakra-ui/react';
import {
  type MouseEventHandler,
  type ReactElement,
  type ReactNode,
  useEffect,
} from 'react';

import { useGetLoggedInUser } from '@revelio/auth';
import {
  DataType,
  type DataTypeResolutionResult,
  FeatureFlag,
  resolveDataType,
} from '@revelio/core';
import { useUnleashFlag } from '@revelio/core/use-unleash-flag';

import { useCurrencyPreference } from '../../../../shared/user-preferences/hooks/useCurrencyPreference';
import { FilterId, type PrimaryFilterId } from '../../filters/types/filters';
import { positionsEntityLookup } from '../../filters/utils/getV2Filters';
import { dimensionSourceToFilterId } from '../../utils/dimension-source-serialization';
import { ChartFallback } from '../Chart/ChartFallback';
import { ChartLoader } from '../Chart/ChartLoader';
import { ChartNotSupported } from '../Chart/ChartNotSupported';
import { useChart } from '../Chart/ChartProvider';
import { useChartMenuContext } from '../Chart/chart-menu/ChartMenuContext';
import { isSelectableChartType } from '../ChartConfig/utils/is-selectable-chart-type';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../PageConfigProvider/types';
import type {
  ChartType,
  CompensationChartType,
  PostingChartType,
  ScreenerChartType,
  SentimentChartType,
  TransitionChartType,
} from '../PageConfigProvider/types/chart';
import {
  type CompensationSecondaryDimension,
  isTransitionSecondaryDimension,
  type PostingSecondaryDimension,
  type SecondaryDimensionValues,
  type TransitionSecondaryDimensionValues,
} from '../PageConfigProvider/types/dimension';
import type {
  DimensionSource,
  PositionedItem,
} from '../PageConfigProvider/types/item';
import {
  isTalentDiscoveryMetric,
  type MetricValues,
  type PayMetricValues,
  type PostingMetricValues,
  type SentimentMetricValues,
  type TransitionMetricValues,
} from '../PageConfigProvider/types/metric';
import { usePageState } from '../PageState/PageStateProvider';
import { isMultiEntitySwitcherChart } from '../PageState/utils/is-multi-entity-switcher-chart';
import { isSingleEntityChart } from '../PageState/utils/is-single-entity-chart';
import { CompensationDataProvider } from './compensation/CompensationDataProvider';
import { CompositionDataProvider } from './composition/CompositionDataProvider';
import {
  isCompositionTimeseries,
  shouldGetSelectionBreakdowns,
} from './composition/composition-utils';
import {
  MultiMetricProvider,
  type MultiMetricProviderResult,
} from './MultiMetricProvider';
import { CompetitiveLandscapeProvider } from './market-conditions/CompetitiveLandscapeProvider';
import { DemandSupplyDataProvider } from './market-conditions/DemandSupplyDataProvider';
import { MarketTightnessDataProvider } from './market-conditions/MarketTightnessDataProvider';
import { PostingDataProvider } from './PostingDataProvider';
import { PostingDiscoveryDataProvider } from './PostingDiscoveryDataProvider';
import { ScreenerDataProvider } from './ScreenerDataProvider';
import { ScreenerMapDataProvider } from './ScreenerMapDataProvider';
import { SentimentDataProvider } from './SentimentDataProvider';
import { SentimentReviewDataProvider } from './SentimentReviewDataProvider';
import { SnowflakeDataProvider } from './SnowflakeDataProvider';
import { TalentDiscoveryDataProvider } from './TalentDiscoveryDataProvider';
import { TransitionDataProvider } from './TransitionDataProvider';
import { TotalSpendDataProvider } from './total-spend/TotalSpendDataProvider';
import { TransitionV3DataProvider } from './transition-v3-data-provider';
import {
  type BreakdownSingleDataSet,
  type DataProviderChildren,
  DataProviderQueryStatus,
  type DataProviderResult,
  type DataProviderSet,
  type MapDataSet,
  type SingleDataSet,
} from './types';
import { getChartDataSourceAccessErrorMessage } from './utils/chart-data-source-access';
import {
  type CompositionRoutableMetric,
  isSentimentSnapshotChartType,
  routesToCompensation,
  routesToComposition,
  routesToPosting,
  routesToScreener,
  routesToSentiment,
  routesToTransition,
} from './utils/data-provider-routing';
import {
  extractMetricsFromHandlebarsFields,
  getHandlebarsFields,
} from './utils/handlebars-field-config';
import { extractMetricsWithChartTypes } from './utils/text-metrics';

const DataProviderWithDownload = ({
  data,
  status,
  error,
  children,
  suppressDownload,
}: DataProviderResult<DataProviderSet> & {
  children: DataProviderChildren<DataProviderSet>;
  suppressDownload?: boolean;
}) => {
  const { setDataProviderSet } = useChartMenuContext();

  useEffect(() => {
    if (suppressDownload) return;
    if (status === DataProviderQueryStatus.Success && data) {
      setDataProviderSet(data);
    }
  }, [data, status, setDataProviderSet, suppressDownload]);

  return children({ data, status, error });
};

const notSupportedDetails = (args: {
  metric?: MetricValues;
  item: { id: string; title?: string | null };
  chartType: ChartType;
  secondaryDimension?: SecondaryDimensionValues;
}) =>
  args.metric
    ? {
        chartId: args.item.id,
        chartTitle: args.item.title ?? undefined,
        chartType: args.chartType,
        metric: args.metric,
        secondaryDimension: args.secondaryDimension,
        reason: 'no_data_provider' as const,
      }
    : undefined;

export const DataProvider = ({
  primaryDimension,
  dimensionSource,
  secondaryDimension,
  chartType,
  children,
  metric,
  suppressDownload,
  doSideEffects = false,
  onClick,
}: {
  primaryDimension?: PrimaryFilterId;
  dimensionSource?: DimensionSource;
  secondaryDimension?: SecondaryDimensionValues;
  chartType: ChartType;
  children: DataProviderChildren<DataProviderSet>;
  metric?: MetricValues;
  suppressDownload?: boolean;
  doSideEffects?: boolean;
  onClick?: MouseEventHandler<HTMLDivElement>;
}) => {
  const { revelioPageId } = useTabContentConfig();
  const { item } = useChart();
  const { pageState } = usePageState();
  const { exchangeRate } = useCurrencyPreference();
  const { loggedInUser } = useGetLoggedInUser();

  const isTransitionsV3Enabled = useUnleashFlag(FeatureFlag.TransitionsV3);

  const dataTypeResolution: DataTypeResolutionResult =
    metric !== undefined && isSelectableChartType(chartType)
      ? resolveDataType(
          chartType,
          metric,
          secondaryDimension,
          item.plot?.dimensionSource
        )
      : {
          compatible: false,
          reason:
            'Data type resolution skipped: metric missing or chart type not selectable',
        };

  const renderFetchResult = (
    data: DataProviderSet | undefined,
    status: DataProviderQueryStatus,
    error?: Error
  ) => {
    return (
      <ChartFallback status={status} onClick={onClick}>
        {doSideEffects ? (
          <DataProviderWithDownload
            data={data}
            status={status}
            error={error}
            suppressDownload={suppressDownload}
          >
            {children}
          </DataProviderWithDownload>
        ) : (
          children({ data, status, error })
        )}
      </ChartFallback>
    );
  };

  const chartNotSupported = (message?: ReactNode) => (
    <ChartNotSupported
      onClick={onClick}
      errorDetails={notSupportedDetails({
        metric,
        item,
        chartType,
        secondaryDimension,
      })}
    >
      {message}
    </ChartNotSupported>
  );

  const accessErrorMessage = !revelioPageId
    ? getChartDataSourceAccessErrorMessage({
        metric,
        chartType,
        secondaryDimension,
        userTabs: loggedInUser.tabs ?? [],
      })
    : undefined;

  if (accessErrorMessage) {
    return renderFetchResult(
      undefined,
      DataProviderQueryStatus.Error,
      new Error(accessErrorMessage)
    );
  }

  // Route sq:* metrics to SnowflakeDataProvider, deriving the savedQueryId
  // from the metric key and using snowflakeConfig from the item if available.
  if (metric?.startsWith('sq:')) {
    const savedQueryId = metric.slice(3);
    const sfConfig = item.plot?.snowflakeConfig ?? {
      savedQueryId,
      fields: [],
    };
    const sfChartType = chartType === 'text' ? 'stacked-bar' : 'gauge';
    return (
      <SnowflakeDataProvider config={sfConfig} chartType={sfChartType}>
        {({ data, status }) => renderFetchResult(data, status)}
      </SnowflakeDataProvider>
    );
  }

  if (metric === 'market_share' || metric === 'target_companies_rec') {
    const ds = item.plot?.dimensionSource;
    const clDimension =
      ds &&
      ds !== 'primary' &&
      dimensionSourceToFilterId(ds) === FilterId.COMPANY
        ? FilterId.COMPANY
        : undefined;

    if (clDimension) {
      return (
        <CompetitiveLandscapeProvider
          primaryDimension={clDimension}
          metrics={[metric]}
        >
          {({ data, status }) =>
            renderFetchResult(data?.byMetric?.[metric], status)
          }
        </CompetitiveLandscapeProvider>
      );
    }
  }

  if (metric === 'positive_review' || metric === 'negative_review') {
    if (!primaryDimension) return <ChartNotSupported onClick={onClick} />;

    return (
      <SentimentReviewDataProvider
        primaryDimension={primaryDimension}
        metric={metric}
      >
        {({ data, status }) => renderFetchResult(data, status)}
      </SentimentReviewDataProvider>
    );
  }

  if (
    metric &&
    isTalentDiscoveryMetric(metric) &&
    revelioPageId === RevelioPageId.POSTING_DISCOVERY
  ) {
    return (
      <PostingDiscoveryDataProvider exchangeRate={exchangeRate}>
        {({ data, status }) => (
          <ChartFallback status={status}>
            <DataProviderWithDownload
              data={data?.tableData}
              status={status}
              suppressDownload={suppressDownload}
            >
              {children}
            </DataProviderWithDownload>
          </ChartFallback>
        )}
      </PostingDiscoveryDataProvider>
    );
  }

  if (metric && isTalentDiscoveryMetric(metric)) {
    return (
      <TalentDiscoveryDataProvider exchangeRate={exchangeRate}>
        {({ data, status }) => {
          if (status === DataProviderQueryStatus.Fetching) {
            return (
              <Box
                position="absolute"
                top="0"
                left="0"
                width="100%"
                height="100%"
                zIndex="4"
              >
                <ChartLoader />
              </Box>
            );
          }

          return (
            <ChartFallback status={status} onClick={onClick}>
              <DataProviderWithDownload
                data={data?.tableData}
                status={status}
                suppressDownload={suppressDownload}
              >
                {children}
              </DataProviderWithDownload>
            </ChartFallback>
          );
        }}
      </TalentDiscoveryDataProvider>
    );
  }

  if (
    (metric === 'demand_supply' || metric === 'demand_supply_percentile') &&
    primaryDimension
  ) {
    return (
      <DemandSupplyDataProvider
        primaryDimension={primaryDimension}
        variant={metric === 'demand_supply_percentile' ? 'percentile' : 'ratio'}
      >
        {({ data, status }) => renderFetchResult(data, status)}
      </DemandSupplyDataProvider>
    );
  }

  if (metric === 'market_tightness' && primaryDimension) {
    return (
      <MarketTightnessDataProvider primaryDimension={primaryDimension}>
        {({ data, status }) => renderFetchResult(data, status)}
      </MarketTightnessDataProvider>
    );
  }

  const isTotalSpendWithBreakdown =
    metric &&
    (metric === 'total_spend_salary' ||
      metric === 'total_spend_compensation') &&
    (secondaryDimension === 'geography' || secondaryDimension === 'role') &&
    (chartType === 'stacked-bar' ||
      chartType === 'grouped-bar' ||
      chartType === 'table' ||
      chartType === 'treemap');

  if (isTotalSpendWithBreakdown) {
    if (!primaryDimension || primaryDimension !== FilterId.COMPANY) {
      return <ChartNotSupported />;
    }

    const totalSpendEntitiesOverride = (() => {
      if (!isSingleEntityChart(item)) return undefined;
      const selectedEntity = pageState.primaryEntities?.[item?.id];
      if (!selectedEntity) return undefined;
      const EntType = positionsEntityLookup[selectedEntity.levelId];
      const EntID = Number(selectedEntity.id);
      if (!EntType || Number.isNaN(EntID)) return undefined;
      return [{ EntType, EntID }];
    })();

    return (
      <TotalSpendDataProvider
        primaryDimension={primaryDimension}
        secondaryDimension={secondaryDimension}
        metric={metric}
        chartType={chartType}
        exchangeRate={exchangeRate}
        entitiesOverride={totalSpendEntitiesOverride}
      >
        {({ data, status }) =>
          doSideEffects ? (
            <DataProviderWithDownload
              data={data}
              status={status}
              suppressDownload={suppressDownload}
            >
              {children}
            </DataProviderWithDownload>
          ) : (
            children({ data, status })
          )
        }
      </TotalSpendDataProvider>
    );
  }

  if (metric && routesToScreener(metric, chartType)) {
    if (!primaryDimension) return <ChartNotSupported />;
    if (chartType === 'table' || chartType === 'treemap') {
      return (
        <ScreenerDataProvider
          primaryDimension={primaryDimension}
          metric={metric}
          chartType={chartType as ScreenerChartType}
        >
          {({
            data,
            status,
          }: DataProviderResult<
            SingleDataSet | BreakdownSingleDataSet | undefined
          >) => renderFetchResult(data, status)}
        </ScreenerDataProvider>
      );
    }

    return (
      <ScreenerMapDataProvider
        primaryDimension={primaryDimension}
        metric={metric}
        chartType={chartType as ScreenerChartType}
      >
        {({ data, status }: DataProviderResult<MapDataSet | undefined>) =>
          renderFetchResult(data, status)
        }
      </ScreenerMapDataProvider>
    );
  }

  if (metric === undefined || !isSelectableChartType(chartType)) {
    return chartNotSupported();
  }

  if (!dataTypeResolution.compatible) {
    return chartNotSupported(dataTypeResolution.reason);
  }

  const getSelectionBreakdowns = shouldGetSelectionBreakdowns(
    chartType,
    revelioPageId
  );
  const isStackedBarOverTime =
    chartType === 'stacked-bar' && item.plot?.dimensionSource === 'timeseries';
  const compositionTimeseriesFlag =
    isCompositionTimeseries(chartType) ||
    secondaryDimension === 'timeseries' ||
    isStackedBarOverTime ||
    chartType === 'text' ||
    chartType === 'gauge';

  const payEntitiesOverride = (() => {
    const shouldUseMultiEntity =
      isMultiEntitySwitcherChart(item) && !revelioPageId;
    const selectedMulti = pageState.multiPrimaryEntities?.[item?.id];
    return shouldUseMultiEntity && selectedMulti?.length
      ? selectedMulti
          .map((entity) => {
            const EntType = positionsEntityLookup[entity.levelId];
            const EntID = Number(entity.id);
            if (!EntType || Number.isNaN(EntID)) return null;
            return { EntType, EntID };
          })
          .filter((e) => e !== null)
      : undefined;
  })();

  const topNDimension =
    item.plot?.dimensionSource === 'primary'
      ? undefined
      : item.plot?.dimensionSource;

  const compositionEntitiesOverride = (() => {
    if (!isStackedBarOverTime || !isSingleEntityChart(item)) return undefined;
    const selectedEntity = pageState.primaryEntities?.[item?.id];
    if (!selectedEntity) return undefined;
    const EntType = positionsEntityLookup[selectedEntity.levelId];
    const EntID = Number(selectedEntity.id);
    if (!EntType || Number.isNaN(EntID)) return undefined;
    return [{ EntType, EntID }];
  })();

  const renderCompositionBranch = (m: CompositionRoutableMetric) => {
    if (!primaryDimension) return <ChartNotSupported />;
    return (
      <CompositionDataProvider
        primaryDimension={primaryDimension}
        dimensionSource={dimensionSource}
        metric={m}
        segment={secondaryDimension}
        isTimeseries={compositionTimeseriesFlag}
        entitiesOverride={compositionEntitiesOverride}
        getSelectionBreakdowns={getSelectionBreakdowns}
        exchangeRate={exchangeRate}
      >
        {({ data, status, error }) => renderFetchResult(data, status, error)}
      </CompositionDataProvider>
    );
  };

  const renderTransitionBranch = (m: TransitionMetricValues) => {
    if (
      !primaryDimension ||
      (secondaryDimension &&
        !isTransitionSecondaryDimension(secondaryDimension))
    )
      return <ChartNotSupported />;
    if (isTransitionsV3Enabled) {
      return (
        <TransitionV3DataProvider
          primaryDimension={primaryDimension}
          secondaryDimension={secondaryDimension}
          metric={m}
        >
          {({ data, status }) => renderFetchResult(data, status)}
        </TransitionV3DataProvider>
      );
    }

    return (
      <TransitionDataProvider
        primaryDimension={primaryDimension}
        metric={m}
        chartType={chartType as TransitionChartType}
        secondaryDimension={
          secondaryDimension as TransitionSecondaryDimensionValues | undefined
        }
      >
        {({ data, status }) => renderFetchResult(data, status)}
      </TransitionDataProvider>
    );
  };

  const renderPostingBranch = (m: PostingMetricValues) => {
    if (!primaryDimension) return <ChartNotSupported />;
    const postingSecondaryDimension: PostingSecondaryDimension | undefined =
      (() => {
        if (chartType === 'line' || chartType === 'volume') return 'timeseries';
        return secondaryDimension as PostingSecondaryDimension | undefined;
      })();
    return (
      <PostingDataProvider
        primaryDimension={primaryDimension}
        secondaryDimension={postingSecondaryDimension}
        metric={m}
        isSnapshot={chartType === 'bar'}
        chartType={chartType as PostingChartType}
        exchangeRate={exchangeRate}
      >
        {({ data, status }) => renderFetchResult(data, status)}
      </PostingDataProvider>
    );
  };

  const renderSentimentBranch = (m: SentimentMetricValues) => {
    if (!primaryDimension) return <ChartNotSupported />;
    return (
      <SentimentDataProvider
        primaryDimension={primaryDimension}
        chartType={chartType as SentimentChartType}
        metric={m}
        isSnapshot={isSentimentSnapshotChartType(chartType)}
      >
        {({ data, status }) => renderFetchResult(data, status)}
      </SentimentDataProvider>
    );
  };

  const renderCompensationBranch = (m: PayMetricValues) => {
    if (!primaryDimension) return <ChartNotSupported />;
    return (
      <CompensationDataProvider
        chartType={chartType as CompensationChartType}
        primaryDimension={primaryDimension}
        secondaryDimension={
          secondaryDimension as CompensationSecondaryDimension | undefined
        }
        topNDimension={topNDimension}
        metric={m}
        exchangeRate={exchangeRate}
        {...(payEntitiesOverride
          ? { entitiesOverride: payEntitiesOverride }
          : {})}
      >
        {({ data, status }) => renderFetchResult(data, status)}
      </CompensationDataProvider>
    );
  };

  const firstMatchingMetricBranch = (
    attempts: Array<() => ReactElement | null>
  ): ReactElement => {
    for (const attempt of attempts) {
      const node = attempt();
      if (node !== null) {
        return node;
      }
    }
    return chartNotSupported();
  };

  switch (dataTypeResolution.resolvedDataType) {
    case DataType.SINGLE:
    case DataType.BREAKDOWN_SINGLE:
    case DataType.FLOW_BREAKDOWN_SINGLE:
    case DataType.TABLE_ROWS: {
      if (!metric) {
        return chartNotSupported();
      }
      return firstMatchingMetricBranch([
        () =>
          routesToComposition(metric, chartType)
            ? renderCompositionBranch(metric)
            : null,
        () =>
          routesToTransition(metric, chartType, secondaryDimension)
            ? renderTransitionBranch(metric)
            : null,
        () =>
          routesToPosting(metric, chartType, secondaryDimension)
            ? renderPostingBranch(metric)
            : null,
        () =>
          routesToSentiment(metric, chartType)
            ? renderSentimentBranch(metric)
            : null,
        () =>
          routesToCompensation(metric, chartType, secondaryDimension)
            ? renderCompensationBranch(metric)
            : null,
      ]);
    }

    case DataType.TIMESERIES: {
      if (!metric) {
        return chartNotSupported();
      }
      return firstMatchingMetricBranch([
        () =>
          routesToComposition(metric, chartType)
            ? renderCompositionBranch(metric)
            : null,
        () =>
          routesToPosting(metric, chartType, secondaryDimension)
            ? renderPostingBranch(metric)
            : null,
        () =>
          routesToSentiment(metric, chartType)
            ? renderSentimentBranch(metric)
            : null,
        () =>
          routesToCompensation(metric, chartType, secondaryDimension)
            ? renderCompensationBranch(metric)
            : null,
      ]);
    }

    case DataType.BREAKDOWN_TIMESERIES: {
      if (!metric) {
        return chartNotSupported();
      }
      return firstMatchingMetricBranch([
        () =>
          routesToComposition(metric, chartType)
            ? renderCompositionBranch(metric)
            : null,
        () =>
          routesToPosting(metric, chartType, secondaryDimension)
            ? renderPostingBranch(metric)
            : null,
      ]);
    }

    case DataType.BOX:
    case DataType.DISTRIBUTION: {
      if (
        metric !== undefined &&
        routesToCompensation(metric, chartType, secondaryDimension)
      ) {
        return renderCompensationBranch(metric);
      }
      return chartNotSupported();
    }

    case DataType.MAP: {
      if (!metric) {
        return chartNotSupported();
      }
      return firstMatchingMetricBranch([
        () =>
          routesToComposition(metric, chartType)
            ? renderCompositionBranch(metric)
            : null,
      ]);
    }

    case DataType.TEXT: {
      return chartNotSupported();
    }

    default: {
      const _exhaustive: never = dataTypeResolution.resolvedDataType;
      return chartNotSupported(`Unhandled DataType: ${_exhaustive}`);
    }
  }
};

export const MultiMetricDataProvider = ({
  item,
  chartType,
  primaryDimension,
  onClick,
  children,
}: {
  item: PositionedItem;
  chartType: ChartType;
  primaryDimension?: PrimaryFilterId;
  onClick?: MouseEventHandler<HTMLDivElement>;
  children: DataProviderChildren<MultiMetricProviderResult>;
}) => {
  const metricsInFields = extractMetricsFromHandlebarsFields(item, chartType);

  if (metricsInFields.length === 0 && chartType !== 'text') {
    return (
      <ChartNotSupported
        onClick={onClick}
        errorDetails={
          item
            ? {
                chartId: item.id,
                chartTitle: item.title ?? undefined,
                chartType,
                reason: 'no_data_provider',
              }
            : undefined
        }
      />
    );
  }

  const textFields = getHandlebarsFields(item, chartType);
  const { metrics, metricChartTypes } =
    extractMetricsWithChartTypes(textFields);

  return (
    <MultiMetricProvider
      primaryDimension={primaryDimension}
      metrics={metrics}
      chartType={chartType}
      metricChartTypes={
        Object.keys(metricChartTypes).length ? metricChartTypes : undefined
      }
    >
      {children}
    </MultiMetricProvider>
  );
};
